import { useState } from "react";
import { toast } from "sonner";
import { Plus, FileText, Trash2 } from "lucide-react";
import { fmtDate, ordinal, openCrmFile, money } from "@/lib/crmApi";
import { QuoteDialog } from "@/components/crm/QuoteDialog";

export const QuotesSection = ({ lead, meta, onAdd, onDelete, saving }) => {
  const [open, setOpen] = useState(false);
  const quotes = lead.quotes || [];
  const openFile = (id) => openCrmFile(id).catch(() => toast.error("Couldn't open the file"));
  return (
    <section data-testid="quotes-section" className="rounded-2xl bg-white border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-900">Quotes sent</h2>
        <button data-testid="quote-add" onClick={() => setOpen(true)} className="h-10 px-4 rounded-full bg-slate-100 hover:bg-slate-200 text-sm font-medium inline-flex items-center gap-1.5 transition-colors">
          <Plus className="h-4 w-4" /> Add quote
        </button>
      </div>
      {quotes.length === 0 ? <p className="text-sm text-slate-400 py-4">No quotes yet.</p> : (
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500">
              <th className="px-5 py-2 font-medium">#</th><th className="px-2 py-2 font-medium">Date sent</th><th className="px-2 py-2 font-medium">Product</th>
              <th className="px-2 py-2 font-medium text-right">Price</th><th className="px-2 py-2 font-medium text-right">Total qty</th><th className="px-2 py-2 font-medium text-right">Total ex-works</th><th className="px-2 py-2 font-medium">Remarks</th><th className="px-2 py-2 font-medium">File</th><th className="px-5 py-2" />
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {quotes.map((q, i) => (
                <tr key={q.id} data-testid={`quote-row-${i}`} className="align-top">
                  <td data-testid={`quote-seq-${i}`} className="px-5 py-3 font-semibold text-slate-900 whitespace-nowrap">{ordinal(q.seq)}</td>
                  <td className="px-2 py-3 whitespace-nowrap">{fmtDate(q.date_sent)}</td>
                  <td className="px-2 py-3">
                    {q.quote_no && <p className="text-xs text-slate-500 font-mono">{q.quote_no}</p>}
                    {q.lines?.length ? (
                      <ul data-testid={`quote-lines-${i}`} className="space-y-0.5">
                        {q.lines.map((l, j) => (
                          <li key={j} className="text-slate-800">{l.description || "—"}<span className="text-xs text-slate-500">
                            {[l.grade, l.thickness_mm && `${l.thickness_mm} mm`, l.qty_kg && `${l.qty_kg.toLocaleString("en-IN")} kg`, l.rate_per_kg != null && `${q.currency} ${l.rate_per_kg}/kg`].filter(Boolean).map((x) => ` · ${x}`).join("")}
                          </span></li>
                        ))}
                      </ul>
                    ) : (q.product || "—")}
                  </td>
                  <td className="px-2 py-3 text-right whitespace-nowrap font-mono">{q.price != null ? `${q.currency} ${q.price.toLocaleString()} ` : "—"}<span className="text-xs text-slate-500 font-sans">{q.price != null && q.unit}</span></td>
                  <td data-testid={`quote-total-qty-${i}`} className="px-2 py-3 text-right whitespace-nowrap font-mono">{q.total_qty_kg ? `${q.total_qty_kg.toLocaleString("en-IN")} kg` : "—"}</td>
                  <td data-testid={`quote-total-value-${i}`} className="px-2 py-3 text-right whitespace-nowrap font-mono font-semibold text-emerald-700">{money(q.total_value, q.currency)}</td>
                  <td className="px-2 py-3 text-slate-600">{q.remarks || "—"}</td>
                  <td className="px-2 py-3">
                    {q.file ? (
                      <button data-testid={`quote-file-${i}`} onClick={() => openFile(q.file.id)} className="inline-flex items-center gap-1 text-blue-700 hover:underline whitespace-nowrap">
                        <FileText className="h-4 w-4" /> Open
                      </button>
                    ) : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <button data-testid={`quote-delete-${i}`} onClick={() => window.confirm(`Remove the ${ordinal(q.seq)} quote?`) && onDelete(q.id)} className="text-slate-300 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && <QuoteDialog leadId={lead.id} meta={meta} onClose={() => setOpen(false)} saving={saving} onSave={(q) => onAdd(q, () => setOpen(false))} />}
    </section>
  );
};
